import { DictType } from "@pma/types"
import { useAuth } from "@stores/auth-store"
import { Avatar, AvatarImage } from "@components/common/avatar"
import { useMutation } from "@tanstack/react-query"
import { toast } from "@hooks/use-toast"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { UserPlus } from "lucide-react"
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "@components/common/popover"
import { Separator } from "@components/common/separator"
import { useEffect, useState } from "react"
import { Button } from "@components/common/button"
import useTranslator from "@hooks/use-translator"
import { SERVER_URL } from "@pma/utils/api-routes"
import axios from "axios"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"
import { useModal } from "@stores/modal-store"
import { Input } from "../common/input"
import { Checkbox } from "../common/checkbox"
import { Label } from "../common/label"

interface Props {
  taskArr: Array<DictType>
}
const BatchAssignToUser = ({ taskArr }: Props) => {
  const modal = useModal()
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  const organizationUsers = authConfig?.organizationUsers || []

  const [selectedUser, setSelectedUser] = useState<DictType>({})
  const [searchText, setSearchText] = useState<string>("")
  const [availableUsersArr, setAvailableUserArr] = useState([])
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set([]))

  const assignUser = async (users: number[]) => {
    queryClient.invalidateQueries({ queryKey: ["show-loader"] })

    return Promise.all(
      taskArr.map((val: any) => {
        const value = val.responsible_id == undefined ? [] : val.responsible_id
        return axios.post(
          SERVER_URL + "/tasks/edit/byCurrentUser",
          {
            responsible_id: [...value, ...users],
            task_id: val.taskId,
            inform_users: true,
          },
          {
            headers: {
              Authorization: `Bearer ${parseCookies()[ACCESS_TOKEN]}`,
            },
          },
        )
      }),
    )
      .then((res) => {
        queryClient.invalidateQueries({ queryKey: ["get-tasks"] })
        queryClient.invalidateQueries({ queryKey: ["clear-selected-tasks"] })
        queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
        queryClient.invalidateQueries({ queryKey: ["clear-selected-group-tasks"] })

        return res
      })
      .catch((error) => error)
  }

  const assignTaskMutation = useMutation({
    mutationKey: ["assign-task-to-users"],
    mutationFn: (users: number[]) => assignUser(users),
    onSuccess: (data) => {},
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      if (errorMessage === "User not invited") {
        promptReInvite()
      }
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error occurred"),
        variant: "error",
      })
    },
  })

  useEffect(() => {
    const arr = organizationUsers.filter((user: DictType) => user.name)
    setAvailableUserArr(arr)
  }, [])

  const promptReInvite = () => {
    modal.onOpen()
    modal.setContext("invite")
    modal.setMetaData(selectedUser)
  }

  const handleCheckboxChange = (id: number) => {
    setSelectedRows((prev) => {
      const newSelected = new Set(prev)
      if (newSelected.has(id)) newSelected.delete(id)
      else newSelected.add(id)

      return newSelected
    })
  }

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const filtered = organizationUsers.filter((user: DictType) =>
        user.name.toLowerCase().includes(searchText.toLowerCase()),
      )
      setAvailableUserArr(filtered)
    }, 300) // Debounce delay in ms

    return () => clearTimeout(delayDebounce) // Cleanup timeout
  }, [searchText])

  const handleSubmit = () => {
    const idArray = Array.from(selectedRows)
    assignTaskMutation.mutate(idArray)
  }

  return (
    <Popover>
      <div className='flex items-center'>
        <Tooltip>
          <TooltipTrigger>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className='flex flex-row items-center justify-center gap-2 hover:bg-slate-300'
                size={"sm"}
              >
                <UserPlus size={14} />
                <span className='hidden min-w-[35px] flex-1 text-left sm:block'>
                  {`${translate("Assign")} ${taskArr.length === 1 ? translate("task to") : translate("tasks to")} ${translate("User")}`}
                </span>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent className='flex items-center gap-1 bg-destructive'>
            {<h4>{translate(`Assigned selected tasks's to the user`)}</h4>}
          </TooltipContent>
        </Tooltip>
      </div>

      <PopoverContent className='max-h-[400px] min-w-fit overflow-y-auto'>
        <Input
          id='column-name'
          value={searchText}
          className={isHebrew ? "rtl" : "ltr"}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={translate("Search User")}
        />
        <Separator className='my-2' />
        <div className=''>
          {availableUsersArr.length > 0 ? (
            availableUsersArr.map((user: DictType, index: number) => {
              return (
                <div key={user?.id}>
                  <div
                    key={user.value}
                    className='my-1 flex cursor-pointer flex-row items-center gap-2 rounded-md p-1 hover:bg-primary/30'
                  >
                    <Checkbox
                      id={`user_${user?.id}`}
                      checked={selectedRows.has(user.id)}
                      onCheckedChange={() => {
                        handleCheckboxChange(user.id)
                        setSelectedUser(user)
                      }}
                    />
                    <Label
                      htmlFor={`user_${user?.id}`}
                      className='flex w-full cursor-pointer items-center justify-start gap-2'
                    >
                      <Avatar className='h-7 w-7'>
                        <AvatarImage
                          src={
                            user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`
                          }
                        />
                      </Avatar>{" "}
                      {user?.name}
                    </Label>
                  </div>
                  {index != availableUsersArr.length - 1 ? <Separator className='my-0.5' /> : null}
                </div>
              )
            })
          ) : (
            <p>{translate("No Users")}</p>
          )}
        </div>
        <Separator className='my-2' />
        <PopoverClose className=' '>
          <Button onClick={handleSubmit}>{translate("Submit")}</Button>
        </PopoverClose>
      </PopoverContent>
    </Popover>
  )
}

export default BatchAssignToUser
